import { BaseCachedApi } from '../../api/BaseCachedApi';
import { Backend } from '../../api/Backend';
import { SettingsKeys } from '../../api/models/TranslationKeys';
import { CustomerSettingsApi, ReminderApi } from '../../api/user';
import { IReminderElementV1 } from '../../api/models/AndromeTV.types';
import { AssetType } from '../../api/models/Shared.types';
import { EpgApiV5 } from '../../api/EpgApiV5';
import { timeStringToMs } from '../utility/DateFormatter';
import ReminderManager from './ReminderManager';
import { ReminderModel, ReminderWorkerMessageItem } from './reminder.model-shared';
import { Log } from '@lightningjs/sdk';
import App from '../../App';
import { ReminderDialog } from '../../components/reminder/ReminderDialog';
import { ReminderDeviceSettingKeys } from '../../api/models/NpvrSettingsKeys';

export default class CachedReminderApi extends BaseCachedApi {
  public readonly reminderManager = new ReminderManager();
  protected override _cache: Map<string, ReminderModel> | undefined;

  private beforeReminderTime?: number;

  constructor(private backend: Backend, appRoot: App) {
    super();
    this.updateGlobalSettings();
    this.reminderManager.start();
    this.reminderManager.subscribe((data: ReminderWorkerMessageItem[]) => {
      appRoot.$openDialog({
        type: ReminderDialog,
        backend: this.backend,
        data: data[0]?.model,
      });
    });
  }

  public reset(backend: Backend) {
    CustomerSettingsApi.value(backend, ReminderDeviceSettingKeys.reminder_before_time)
      .then(res => {
        this.beforeReminderTime = parseInt(res.value, 10) * 1000;
      })
      .catch(e => {
        Log.error('[ReminderService]', 'failed to fetch deviceSetting reminderBeforeTime, using default 0', e);
      });
    this.bypassCache(true);
    this.checkCache(backend).then(r => {});
  }

  public updateReminderBeforeTime(newTime: string) {
    this.beforeReminderTime = parseInt(newTime, 10) * 1000 ?? 0;
  }

  public hasReminder(backend: Backend, id: string) {
    return this.checkCache(backend).then(() => {
      return !!this._cache?.has(id);
    });
  }

  public toggleReminder(backend: Backend, type: AssetType, id: string): Promise<boolean> {
    if (type !== AssetType.SCHEDULE) {
      return Promise.resolve(false);
    }

    return this.checkCache(backend).then(() => {
      if (this._cache?.has(id)) {
        this.deleteReminder(backend, type, id);
        return false;
      } else {
        this.setReminder(backend, type, id);
        return true;
      }
    });
  }

  public getReminderList(backend: Backend) {
    return this.checkCache(backend).then(() => {
      let result = [...this._cache!.values()];
      if (!result) return [];

      result = result.filter(
        reminder => timeStringToMs(reminder.schedule.schedule.published.start) > new Date().getTime()
      );
      result.sort((a, b) => {
        const aStart = timeStringToMs(a.schedule.schedule.published.start);
        const bStart = timeStringToMs(b.schedule.schedule.published.start);
        const aEnd = timeStringToMs(a.schedule.schedule.published.end);
        const bEnd = timeStringToMs(b.schedule.schedule.published.end);
        if (aStart < bStart) {
          return -1;
        } else if (aStart > bStart) {
          return 1;
        } else {
          if (aEnd < bEnd) {
            return -1;
          } else if (aEnd > bEnd) {
            return 1;
          } else {
            return 0;
          }
        }
      });
      return result;
    });
  }

  private deleteReminder(backend: Backend, type: AssetType, id: string) {
    ReminderApi.delete(backend, { type: type, id: id, profileId: backend.deviceProfile?.id }).then(() => {
      this._cache?.delete(id);
    });
  }

  private setReminder(backend: Backend, type: AssetType, id: string) {
    const beforeMinutes = (this.beforeReminderTime ?? 0) / 1000 / 60;
    EpgApiV5.schedule(backend, id).then(result => {
      const reminder: IReminderElementV1 = { id, type, minutes: beforeMinutes };
      const reminderModel = new ReminderModel(reminder, result);
      this._cache?.set(id, reminderModel);
      this.reminderManager.addReminder(this.createReminderWorkerItem(backend, reminderModel));
    });

    ReminderApi.update(
      backend,
      {
        type: type,
        id: id,
        profileId: backend.deviceProfile?.id,
      },
      { minutes: beforeMinutes }
    ).then(() => {});
  }

  private updateGlobalSettings() {
    this.updateExpirations(
      this.backend.globalSettings.numberValue(SettingsKeys.reminderCacheExpiration) ?? 600000,
      this.backend.globalSettings.numberValue(SettingsKeys.reminderCacheErrorExpiration) ?? 300000
    );
  }

  override fillCache(backend: Backend): Promise<unknown> {
    this._cache = new Map<string, ReminderModel>();
    return ReminderApi.list(backend, { profileId: backend.deviceProfile?.id })
      .then((result: IReminderElementV1[]) => {
        const scheduleRequests: Promise<ReminderModel | undefined>[] = [];
        result.forEach(reminder => {
          if (reminder.type === AssetType.SCHEDULE) {
            scheduleRequests.push(this.getReminderModel(backend, reminder));
          }
        });

        return Promise.all(scheduleRequests);
      })
      .then(() => {
        Log.info('[Check Cache]', 'triggered');
        this.scheduleWorkerReminders(backend, false);
      });
  }

  private getReminderModel(backend: Backend, reminder: IReminderElementV1) {
    return EpgApiV5.schedule(backend, reminder.id).then(schedule => {
      if (schedule?.schedule?.published && timeStringToMs(schedule.schedule.published.start) > new Date().getTime()) {
        const model = new ReminderModel(reminder, schedule);
        this._cache?.set(schedule.schedule.scheduleId, model);
        return model;
      }
      return undefined;
    });
  }

  private scheduleWorkerReminders(backend: Backend, checkCache = true) {
    const promise = checkCache ? this.checkCache(backend) : Promise.resolve();

    return promise.then(() => {
      const workerItems: ReminderWorkerMessageItem[] = [];
      this._cache?.forEach(value => {
        workerItems.push(this.createReminderWorkerItem(backend, value));
      });
      this.reminderManager.renewReminders(workerItems);
    });
  }

  private createReminderWorkerItem(_backend: Backend, value: ReminderModel): ReminderWorkerMessageItem {
    let time = value.schedule?.schedule?.published?.start
      ? parseInt(value.schedule.schedule.published.start, 10) * 1000 // seconds to ms
      : 0;
    if (value.minutes) {
      time -= value.minutes * 1000 * 60;
      time = Math.max(time, 0);
    }

    return {
      model: value,
      time: time, // Date.now() + 10000,
    };
  }
}
